import type {
  Allocation,
  AiModelId,
  AiModelSettings,
  AiModelTestResult,
  BootstrapData,
  ConstraintSet,
  CommitteeReport,
  DashboardMarketData,
  FundDashboardData,
  FundResearchBundle,
  DataSource,
  InstitutionProfile,
  MemoryRecord,
  MaterialLibraryStats,
  PortfolioCalibration,
  PortfolioCalibrationResult,
  ResearchTemplate,
  ResearchBundle,
  ResearchMaterial,
  RagScope,
  RagSearchResult,
} from "../../shared/types";

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  const response = await fetch(path, { ...init, headers, credentials: "same-origin" });
  const payload = await response.json().catch(() => ({})) as T & { error?: string };
  if (!response.ok) throw new ApiError(payload.error || `请求失败（${response.status}）`, response.status);
  return payload;
}

export const api = {
  login: (username: string, password: string) => request<{ user: { username: string; role: string } }>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  }),
  logout: () => request<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
  me: () => request<{ user: { username: string; role: string } }>("/api/auth/me"),
  bootstrap: () => request<BootstrapData>("/api/bootstrap"),
  dashboardMarket: (refresh = false) => request<DashboardMarketData>(`/api/dashboard/market${refresh ? "?refresh=1" : ""}`),
  dashboardFunds: (refresh = false) => request<FundDashboardData>(`/api/dashboard/funds${refresh ? "?refresh=1" : ""}`),
  saveProfile: (profile: InstitutionProfile) => request<{ profile: InstitutionProfile }>("/api/settings/profile", {
    method: "PUT",
    body: JSON.stringify(profile),
  }),
  saveConstraints: (constraints: ConstraintSet) => request<{ constraints: ConstraintSet }>("/api/settings/constraints", {
    method: "PUT",
    body: JSON.stringify(constraints),
  }),
  saveAiModel: (model: AiModelId) => request<{ settings: AiModelSettings }>("/api/settings/model", {
    method: "PUT",
    body: JSON.stringify({ model }),
  }),
  testAiModel: (model: AiModelId) => request<AiModelTestResult>("/api/settings/model/test", {
    method: "POST",
    body: JSON.stringify({ model }),
  }),
  saveSource: (source: Partial<DataSource>) => request<{ source: DataSource }>("/api/sources", {
    method: "POST",
    body: JSON.stringify(source),
  }),
  updateSource: (id: string, source: Partial<DataSource>) => request<{ source: DataSource }>(`/api/sources/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(source),
  }),
  deleteSource: (id: string) => request<{ ok: boolean }>(`/api/sources/${encodeURIComponent(id)}`, { method: "DELETE" }),
  checkSource: (id: string) => request<{ source: DataSource; health: { ok: boolean; latencyMs: number; detail: string; checkedAt: string } }>(`/api/sources/${encodeURIComponent(id)}/check`, { method: "POST" }),
  saveMemory: (memory: Partial<MemoryRecord>) => request<{ record: MemoryRecord }>("/api/memories", {
    method: "POST",
    body: JSON.stringify(memory),
  }),
  saveTemplate: (template: Partial<ResearchTemplate>) => request<{ template: ResearchTemplate }>("/api/templates", {
    method: "POST",
    body: JSON.stringify(template),
  }),
  deleteTemplate: (id: string) => request<{ ok: boolean }>(`/api/templates/${encodeURIComponent(id)}`, { method: "DELETE" }),
  optimize: (allocation: Allocation, constraints: ConstraintSet) => request<{
    current: Allocation;
    target: Allocation;
    expectedReturn: number;
    volatility: number;
    withinRiskBudget: boolean;
    trades: Array<{ asset: string; change: number }>;
  }>("/api/portfolio/optimize", {
    method: "POST",
    body: JSON.stringify({ allocation, constraints }),
  }),
  research: (task: string, prompt: string, context?: unknown) => request<{ content: string; model: string }>("/api/ai/research", {
    method: "POST",
    body: JSON.stringify({ task, prompt, context }),
  }),
  generateResearchReport: (query: string, refresh = false, templateId?: string) => request<ResearchBundle>("/api/research/report", {
    method: "POST",
    body: JSON.stringify({ query, refresh, templateId }),
  }),
  generateFundResearch: (query: string, refresh = false) => request<FundResearchBundle>("/api/funds/research", {
    method: "POST",
    body: JSON.stringify({ query, refresh }),
  }),
  listMaterials: () => request<{ materials: ResearchMaterial[] }>("/api/materials"),
  materialStats: () => request<{ stats: MaterialLibraryStats }>("/api/materials/stats"),
  searchMaterials: (query: string, scope: RagScope, limit = 8) => request<RagSearchResult>("/api/materials/search", { method: "POST", body: JSON.stringify({ query, scope, limit }) }),
  saveMaterial: (material: Partial<ResearchMaterial>) => request<{ material: ResearchMaterial }>("/api/materials", { method: "POST", body: JSON.stringify(material) }),
  analyzeMaterial: (id: string) => request<{ material: ResearchMaterial; model: string }>(`/api/materials/${encodeURIComponent(id)}/analyze`, { method: "POST" }),
  reindexMaterial: (id: string) => request<{ material: ResearchMaterial }>(`/api/materials/${encodeURIComponent(id)}/reindex`, { method: "POST" }),
  deleteMaterial: (id: string) => request<{ ok: boolean }>(`/api/materials/${encodeURIComponent(id)}`, { method: "DELETE" }),
  updateMemory: (id: string, memory: Partial<MemoryRecord>) => request<{ record: MemoryRecord }>(`/api/memories/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(memory),
  }),
  deleteMemory: (id: string) => request<{ ok: boolean }>(`/api/memories/${encodeURIComponent(id)}`, { method: "DELETE" }),
  calibrate: (calibration: PortfolioCalibration, constraints: ConstraintSet) => request<PortfolioCalibrationResult>("/api/portfolio/calibrate", {
    method: "POST",
    body: JSON.stringify({ calibration, constraints }),
  }),
  runCommittee: (input: { fundQuery?: string; motion: string; horizonDays: 7 | 14; decisionType: string; decisionMode: "探索讨论" | "形成建议" | "提交决议"; investmentHorizon: "两周" | "一个季度" | "一年"; urgency: "常规" | "临时事件" | "风险处置"; requiredQuestions: string[]; guardrails: string[]; focusRoles: string[] }) => request<CommitteeReport>("/api/committee/session", {
    method: "POST",
    body: JSON.stringify(input),
  }),
};
